import { useEffect, useRef, useState, type FormEvent } from "react"
import Link from "next/link"

import { useSignUp } from "@clerk/nextjs/legacy"
import type { SignUpResource } from "@clerk/shared/types"
import { Button, Input, Label } from "@coval/ui"

import { TRACKING_EVENTS, useAnalytics } from "@/lib/analytics"
import { logger } from "@/lib/logger"

import { ClerkAuthFormSkeleton } from "../auth-loading"
import {
  getInvitationSignUpDiagnostics,
  getInvitationSignUpNextStep,
  type InvitationProfileField,
} from "../utils/invitationSignUp"
import {
  extractClerkError,
  isClerkSessionExistsError,
  shouldReportClerkAuthError,
} from "../utils/signInErrors"
import { consumeSignupEmail } from "../utils/signupEmailHandoff"
import { ClerkInvitationEmailStep } from "./ClerkInvitationEmailStep"
import { ClerkInvitationProfileStep } from "./ClerkInvitationProfileStep"
import { GoogleOAuthButton } from "./GoogleOAuthButton"

const log = logger("ClerkSignupForm")

type Step = "credentials" | "invitation_email" | "profile" | "verification"
type InvitationEmailMode = "account" | "enterprise_sso"

interface ClerkSignupFormProps {
  onComplete: () => void
  signInHref: string
  redirectUrlComplete: string
  oauthCallbackUrl?: string
  invitationTicket?: string
  /**
   * True when the user arrived from the marketing pricing page (which links to
   * `/signup?source=pricing`). Surfaces the free trial in the headline rather
   * than waiting until the payment step.
   */
  fromPricing?: boolean
  /**
   * Email to pre-fill the field with, carried over from the sign-in form when a
   * user with no account clicks "Create an account". Seeds the initial value
   * only; the user can still edit it.
   */
  initialEmail?: string
}

export function ClerkSignupForm({
  onComplete,
  signInHref,
  redirectUrlComplete,
  oauthCallbackUrl = "/sso-callback",
  invitationTicket,
  fromPricing,
  initialEmail,
}: ClerkSignupFormProps) {
  const { isLoaded, signUp, setActive } = useSignUp()
  const [step, setStep] = useState<Step>("credentials")
  const [email, setEmail] = useState(initialEmail ?? "")
  const hasReadEmailHandoff = useRef(false)
  useEffect(() => {
    if (hasReadEmailHandoff.current) {
      return
    }
    hasReadEmailHandoff.current = true
    const carriedEmail = consumeSignupEmail()
    if (!initialEmail && carriedEmail) {
      setEmail((current) => current || carriedEmail)
    }
  }, [initialEmail])
  const [password, setPassword] = useState("")
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [requiredProfileFields, setRequiredProfileFields] = useState<
    InvitationProfileField[]
  >([])
  const [invitationEmailMode, setInvitationEmailMode] =
    useState<InvitationEmailMode>("account")
  const [code, setCode] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [oauthPending, setOauthPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { trackEvent } = useAnalytics()
  // Tracks whether the user has typed into either field at least once. The
  // funnel needs PAGE_VIEWED → FORM_FIRST_INPUT → CREDENTIALS_SUBMITTED to
  // distinguish "saw the page and left" from "started filling and gave up".
  const hasTrackedFirstInputRef = useRef(false)
  const hasTrackedPageViewRef = useRef(false)
  const hasTrackedVerificationViewRef = useRef(false)

  useEffect(() => {
    // Fire only once the Clerk SDK is loaded — before that the form is a
    // Skeleton, not the real fields, and counting it as a view would inflate
    // the top of the funnel.
    if (!isLoaded || hasTrackedPageViewRef.current) {
      return
    }
    hasTrackedPageViewRef.current = true
    trackEvent(TRACKING_EVENTS.SIGNUP.PAGE_VIEWED, {})
  }, [isLoaded, trackEvent])

  useEffect(() => {
    if (step !== "verification" || hasTrackedVerificationViewRef.current) {
      return
    }
    hasTrackedVerificationViewRef.current = true
    trackEvent(TRACKING_EVENTS.SIGNUP.VERIFICATION_PAGE_VIEWED, {})
  }, [step, trackEvent])

  function trackFirstInputOnce() {
    if (hasTrackedFirstInputRef.current) {
      return
    }
    hasTrackedFirstInputRef.current = true
    trackEvent(TRACKING_EVENTS.SIGNUP.FORM_FIRST_INPUT, {})
  }

  async function handleGoogleSignUp() {
    if (!signUp || oauthPending) {
      return
    }
    setError(null)
    setOauthPending(true)
    trackEvent(TRACKING_EVENTS.SIGNUP.GOOGLE_OAUTH_CLICKED, {})
    try {
      await signUp.authenticateWithRedirect({
        strategy: "oauth_google",
        redirectUrl: oauthCallbackUrl,
        redirectUrlComplete,
      })
    } catch (err) {
      if (isClerkSessionExistsError(err)) {
        onComplete()
        return
      }
      if (shouldReportClerkAuthError(err)) {
        log.error("google signup failed", err)
      }
      setError(extractClerkError(err, "Failed to start Google sign-up."))
      setOauthPending(false)
    }
  }

  if (!isLoaded) {
    return <ClerkAuthFormSkeleton headingWidthClassName="w-48" />
  }

  async function handleInvitationAttempt(attempt: SignUpResource) {
    const nextStep = getInvitationSignUpNextStep(attempt)

    if (nextStep.kind !== "complete") {
      log.warn(
        "invitation signup requires an additional step",
        getInvitationSignUpDiagnostics(attempt),
      )
    }

    switch (nextStep.kind) {
      case "complete":
        if (!setActive) {
          return
        }
        await setActive({ session: nextStep.sessionId })
        onComplete()
        return
      case "collect_profile":
        setRequiredProfileFields(nextStep.fields)
        setStep("profile")
        return
      case "collect_email":
        setEmail("")
        setInvitationEmailMode("account")
        setStep("invitation_email")
        return
      case "enterprise_sso":
        setEmail(nextStep.emailAddress ?? "")
        setInvitationEmailMode("enterprise_sso")
        setStep("invitation_email")
        return
      case "verify_email":
        setEmail(nextStep.emailAddress)
        setCode("")
        await attempt.prepareEmailAddressVerification({
          strategy: "email_code",
        })
        setStep("verification")
        return
      case "abandoned":
        trackEvent(TRACKING_EVENTS.SIGNUP.CREDENTIALS_FAILED, {
          error_kind: "abandoned_invitation",
        })
        setError(
          "This invitation is no longer active. Ask your organization administrator for a new invitation.",
        )
        return
      case "unsupported": {
        const diagnostics = getInvitationSignUpDiagnostics(attempt)
        log.error(
          "invitation signup could not continue",
          new Error("Unsupported Clerk invitation signup state"),
          diagnostics,
        )
        trackEvent(TRACKING_EVENTS.SIGNUP.CREDENTIALS_FAILED, {
          error_kind: "unsupported_invitation_requirement",
        })
        setError(
          "This invitation requires an additional account step we couldn't complete. Contact Coval support.",
        )
      }
    }
  }

  async function handleInvitationEmailSubmit(
    event: FormEvent<HTMLFormElement>,
  ) {
    event.preventDefault()
    if (!signUp || !invitationTicket || submitting || oauthPending) {
      return
    }
    const trimmedEmail = email.trim()
    if (!trimmedEmail) {
      return
    }
    setError(null)
    setSubmitting(true)

    try {
      if (invitationEmailMode === "enterprise_sso") {
        setOauthPending(true)
        await signUp.authenticateWithRedirect({
          strategy: "enterprise_sso",
          emailAddress: trimmedEmail,
          redirectUrl: oauthCallbackUrl,
          redirectUrlComplete,
          continueSignUp: true,
        })
        return
      }

      const attempt = await signUp.update({ emailAddress: trimmedEmail })
      await handleInvitationAttempt(attempt)
    } catch (err) {
      if (shouldReportClerkAuthError(err)) {
        log.error(
          invitationEmailMode === "enterprise_sso"
            ? "invitation enterprise SSO continuation failed"
            : "invitation email update failed",
          err,
        )
      }
      setError(
        extractClerkError(
          err,
          invitationEmailMode === "enterprise_sso"
            ? "We couldn't start your company sign-in. Try again."
            : "We couldn't save your email address. Try again.",
        ),
      )
    } finally {
      setSubmitting(false)
      setOauthPending(false)
    }
  }

  async function handleCredentialsSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!signUp || submitting || (invitationTicket && !setActive)) {
      return
    }
    setError(null)
    setSubmitting(true)
    trackEvent(TRACKING_EVENTS.SIGNUP.CREDENTIALS_SUBMITTED, {})
    try {
      if (invitationTicket) {
        const attempt = await signUp.create({
          strategy: "ticket",
          ticket: invitationTicket,
          password,
        })
        await handleInvitationAttempt(attempt)
        return
      }

      await signUp.create({
        emailAddress: email.trim(),
        password,
      })
      await signUp.prepareEmailAddressVerification({ strategy: "email_code" })
      setStep("verification")
    } catch (err) {
      if (isClerkSessionExistsError(err)) {
        onComplete()
        return
      }
      if (shouldReportClerkAuthError(err)) {
        log.error("signup create failed", err)
      }
      // `error_kind`: "expected" covers known validation rejections (e.g.
      // duplicate email, weak password). Anything else is a real error and
      // gets logged too — keeping them separate keeps the funnel honest.
      trackEvent(TRACKING_EVENTS.SIGNUP.CREDENTIALS_FAILED, {
        error_kind: shouldReportClerkAuthError(err) ? "unexpected" : "expected",
      })
      setError(
        extractClerkError(err, "Failed to create your account. Try again."),
      )
    } finally {
      setSubmitting(false)
    }
  }

  async function handleProfileSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!signUp || !invitationTicket || submitting) {
      return
    }
    setError(null)
    setSubmitting(true)

    try {
      const attempt = await signUp.update({
        ...(requiredProfileFields.includes("first_name")
          ? { firstName: firstName.trim() }
          : {}),
        ...(requiredProfileFields.includes("last_name")
          ? { lastName: lastName.trim() }
          : {}),
      })
      await handleInvitationAttempt(attempt)
    } catch (err) {
      if (shouldReportClerkAuthError(err)) {
        log.error("invitation profile update failed", err)
      }
      setError(
        extractClerkError(
          err,
          "We couldn't save your account information. Try again.",
        ),
      )
    } finally {
      setSubmitting(false)
    }
  }

  async function handleVerificationSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!signUp || !setActive || submitting) {
      return
    }
    setError(null)
    setSubmitting(true)
    trackEvent(TRACKING_EVENTS.SIGNUP.VERIFICATION_SUBMITTED, {})
    try {
      const attempt = await signUp.attemptEmailAddressVerification({ code })
      if (invitationTicket) {
        await handleInvitationAttempt(attempt)
        return
      }
      if (attempt.status !== "complete" || !attempt.createdSessionId) {
        trackEvent(TRACKING_EVENTS.SIGNUP.VERIFICATION_FAILED, {
          reason: "incomplete",
        })
        setError(
          "We couldn't verify that code. Check your email and try again.",
        )
        return
      }
      await setActive({ session: attempt.createdSessionId })
      onComplete()
    } catch (err) {
      if (shouldReportClerkAuthError(err)) {
        log.error("signup verification failed", err)
      }
      trackEvent(TRACKING_EVENTS.SIGNUP.VERIFICATION_FAILED, {
        reason: shouldReportClerkAuthError(err) ? "unexpected" : "expected",
      })
      setError(extractClerkError(err, "Invalid verification code."))
    } finally {
      setSubmitting(false)
    }
  }

  async function handleResend() {
    if (!signUp || submitting) {
      return
    }
    setError(null)
    setSubmitting(true)
    trackEvent(TRACKING_EVENTS.SIGNUP.VERIFICATION_RESEND_CLICKED, {})
    try {
      await signUp.prepareEmailAddressVerification({ strategy: "email_code" })
    } catch (err) {
      if (shouldReportClerkAuthError(err)) {
        log.error("signup resend code failed", err)
      }
      setError(extractClerkError(err, "Could not resend the code."))
    } finally {
      setSubmitting(false)
    }
  }

  if (step === "invitation_email") {
    const requiresEnterpriseSso = invitationEmailMode === "enterprise_sso"

    return (
      <ClerkInvitationEmailStep
        email={email}
        error={error}
        oauthPending={oauthPending}
        requiresEnterpriseSso={requiresEnterpriseSso}
        submitting={submitting}
        onEmailChange={setEmail}
        onSubmit={(event) => {
          void handleInvitationEmailSubmit(event)
        }}
      />
    )
  }

  if (step === "profile") {
    return (
      <ClerkInvitationProfileStep
        error={error}
        firstName={firstName}
        lastName={lastName}
        requiredFields={requiredProfileFields}
        submitting={submitting}
        onFirstNameChange={setFirstName}
        onLastNameChange={setLastName}
        onSubmit={(event) => {
          void handleProfileSubmit(event)
        }}
      />
    )
  }

  if (step === "verification") {
    return (
      <form
        onSubmit={(event) => {
          void handleVerificationSubmit(event)
        }}
        className="space-y-6"
        aria-describedby={error ? "signup-error" : undefined}
      >
        <div className="space-y-1">
          <h1
            className="text-xl font-medium text-coval-text text-center md:text-left"
            data-testid="signup-verification-heading"
          >
            Check your email
          </h1>
          <p className="text-sm text-coval-text-muted">
            We sent a 6-digit code to{" "}
            <strong data-testid="signup-verification-email">{email}</strong>.
          </p>
        </div>

        <div className="space-y-1">
          <Label className="font-medium" htmlFor="signup-code">
            Verification code
          </Label>
          <Input
            id="signup-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(event) => setCode(event.target.value.trim())}
            required
            aria-label="Verification code"
            data-testid="signup-verification-code-input"
          />
        </div>

        {error && (
          <p
            id="signup-error"
            role="alert"
            className="rounded-md border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-500"
          >
            {error}
          </p>
        )}

        <Button
          type="submit"
          className="w-full"
          disabled={submitting || code.length === 0}
          aria-label="Verify email"
          data-testid="signup-verify-button"
        >
          {submitting ? "Verifying…" : "Verify email"}
        </Button>

        <div className="flex items-center justify-between text-sm">
          <button
            type="button"
            className="text-coval-text-muted underline hover:text-coval-text disabled:opacity-50"
            onClick={() => {
              void handleResend()
            }}
            disabled={submitting}
            data-testid="signup-resend-code-button"
          >
            Resend code
          </button>
          {!invitationTicket && (
            <button
              type="button"
              className="text-coval-text-muted underline hover:text-coval-text"
              onClick={() => {
                trackEvent(
                  TRACKING_EVENTS.SIGNUP.VERIFICATION_CHANGE_EMAIL_CLICKED,
                  {},
                )
                setStep("credentials")
                setCode("")
                setError(null)
              }}
              data-testid="signup-change-email-button"
            >
              Use a different email
            </button>
          )}
        </div>
      </form>
    )
  }

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h1
          className="text-xl font-medium text-coval-text text-center md:text-left"
          data-testid="signup-credentials-heading"
        >
          {invitationTicket
            ? "Create your account"
            : fromPricing
              ? "Create your Coval account to start your free trial."
              : "Create your Coval account"}
        </h1>
        {invitationTicket && (
          <p className="text-sm text-coval-text-muted">
            Set a password to accept your organization invitation.
          </p>
        )}
      </div>

      {!invitationTicket && (
        <>
          <GoogleOAuthButton
            onClick={() => {
              void handleGoogleSignUp()
            }}
            disabled={submitting || oauthPending}
            label={oauthPending ? "Redirecting…" : "Continue with Google"}
          />

          <div className="flex items-center gap-3" aria-hidden>
            <div className="h-px flex-1 bg-coval-border" />
            <span className="text-xs text-coval-text-muted">or</span>
            <div className="h-px flex-1 bg-coval-border" />
          </div>
        </>
      )}

      <form
        onSubmit={(event) => {
          void handleCredentialsSubmit(event)
        }}
        className="space-y-6"
        aria-describedby={error ? "signup-error" : undefined}
      >
        {!invitationTicket && (
          <div className="space-y-1">
            <Label className="font-medium" htmlFor="signup-email">
              Work email
            </Label>
            <Input
              id="signup-email"
              name="username"
              type="email"
              autoComplete="username"
              placeholder="you@company.com"
              value={email}
              onChange={(event) => {
                trackFirstInputOnce()
                setEmail(event.target.value)
              }}
              required
              aria-label="Work email"
              data-testid="signup-email-input"
            />
          </div>
        )}

        <div className="space-y-1">
          <Label className="font-medium" htmlFor="signup-password">
            Password
          </Label>
          <Input
            id="signup-password"
            name="new-password"
            type="password"
            autoComplete="new-password"
            placeholder="At least 8 characters"
            value={password}
            onChange={(event) => {
              trackFirstInputOnce()
              setPassword(event.target.value)
            }}
            required
            minLength={8}
            aria-label="Password"
            data-testid="signup-password-input"
          />
        </div>

        {/*
        Clerk's CAPTCHA widget renders into this container when the dev
        instance has bot protection on. `clerk-captcha` is Clerk's documented
        anchor id.
      */}
        <div id="clerk-captcha" />

        {error && (
          <p
            id="signup-error"
            role="alert"
            className="rounded-md border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-500"
          >
            {error}
          </p>
        )}

        <Button
          type="submit"
          className="w-full"
          disabled={
            submitting ||
            oauthPending ||
            !password ||
            (!invitationTicket && !email)
          }
          aria-label="Continue"
          data-testid="signup-continue-button"
        >
          {submitting ? "Creating account…" : "Continue"}
        </Button>

        <p className="text-sm text-coval-text-muted">
          Already have an account?{" "}
          <Link href={signInHref} className="underline hover:text-coval-text">
            Sign in
          </Link>
        </p>
      </form>
    </div>
  )
}
